import type { MonitorOperationType } from "./shared";
import type { ClientBase } from "pg";
import type { ElementOf } from "ts-essentials";
import type { Queryable } from "zapatos/db";

export * from "./shared";
import {
  MonitoredResourceType,
  MonitorConditionTypes,
  MonitorStatus,
} from "./shared";

export enum ChannelType {
  Slack = "slack",
  Email = "email",
  PagerDuty = "pagerduty",
  Webhook = "webhook",
  Sms = "sms",
}

export function assertedChannelType(channelType: string): ChannelType {
  if (!Object.values(ChannelType).includes(channelType as ChannelType)) {
    throw new Error(`Unknown channel type ${channelType}`);
  }
  return channelType as ChannelType;
}

type SlackChannelConfig = {
  slackChannelId: string;
  name: string;
};

type SmsChannelConfig = {
  phoneNumber: string;
  name: string;
};

type EmailConfig = { emailAddress: string };

export type CustomHeaders = Record<
  string,
  string | { value: string; isSecret: boolean }
>;

type WebhookConfig = {
  url: string;
  name: string;
  headers?: CustomHeaders;
};

type ChannelTypeToChannelConfigMapping = {
  [ChannelType.Slack]: SlackChannelConfig;
  [ChannelType.Sms]: SmsChannelConfig;
  [ChannelType.Email]: EmailConfig;
  [ChannelType.PagerDuty]: { severity: string };
  [ChannelType.Webhook]: WebhookConfig;
};

export type ChannelConfig = {
  [C in ChannelType]: ChannelTypeToChannelConfigMapping[C];
};

export type StoredMonitorCondition<TThresholds = unknown> = {
  id: string;
  workspace_id: string;
  evaluation_type: string;
  evaluation_trigger: string;
  type: string;
  enabled: boolean;
  error_value: TThresholds | null;
  warning_value: TThresholds | null;
  resource_id: string;
  resource_type: MonitoredResourceType;
  isTemplate?: boolean;
};

/**
 * This is where you register any "parent" resource types. For example, a destination is the
 * parent of a sync. Parents are used for monitor condition _templates_ i.e. defaults for all child
 * resources of the parent.
 */
export enum ParentResourceTypes {
  Destination = "Destination",
  WorkspaceJourneyDefault = "WorkspaceJourneyDefault",
}

export const ParentResourceFriendlyNames: {
  [P in ParentResourceTypes]: string;
} = {
  [ParentResourceTypes.Destination]: "Destination",
  [ParentResourceTypes.WorkspaceJourneyDefault]: "Workspace",
};

export const MonitoredResourceToParentResourceType: {
  [M in MonitoredResourceType]: ParentResourceTypes | null;
} = {
  [MonitoredResourceType.Sync]: ParentResourceTypes.Destination,
  [MonitoredResourceType.EventSource]: null,
  [MonitoredResourceType.EventSync]: null,
  [MonitoredResourceType.Journey]: ParentResourceTypes.WorkspaceJourneyDefault,
  [MonitoredResourceType.IDR]: null,
  [MonitoredResourceType.EventStreamingSync]: null,
  [MonitoredResourceType.DecisionEngineFlow]: null,
} as const;

// Don't touch these
export enum EvaluationType {
  Point = "point",
  Batch = "batch",
  Manual = "manual",
}

// Don't touch these
export enum EvaluationTrigger {
  Discrete = "discrete",
  Continuous = "continuous",
}

/**
 * This is where we define the input types used to evaluate each monitored resource type. This
 * will be the shape of args passed to SQS when enqueuing a monitor condition.
 */
type MonitoredResourceInputsTypeMapping = {
  [MonitoredResourceType.Sync]: { syncRequestId: string | null };
  [MonitoredResourceType.EventSource]: { eventSourceId: string };
  [MonitoredResourceType.EventSync]: { runId: string | null };
  [MonitoredResourceType.Journey]: { runId: string | null };
  [MonitoredResourceType.IDR]: { runId: string | null };
  [MonitoredResourceType.EventStreamingSync]: { syncId: string | null };
  [MonitoredResourceType.DecisionEngineFlow]: { flowRunId: string | null };
};

/**
 * This is where we map monitored resource types to their inputs. If you are getting
 * an error here, it's likely because you are missing an input for a monitored resource type.
 */
export type MonitoredResourceInputs = {
  [T in MonitoredResourceType]: MonitoredResourceInputsTypeMapping[T];
};

/**
 * Here we dictate how and when each monitor condition is evaluated.
 */
export const MonitorConditionEvaluationProperties: {
  [k in MonitorConditionTypes]: {
    EvaluationTrigger: EvaluationTrigger;
    EvaluationType: EvaluationType;
  };
} = {
  [MonitorConditionTypes.SyncRejectedRows]: {
    EvaluationTrigger: EvaluationTrigger.Discrete,
    EvaluationType: EvaluationType.Point,
  },
  [MonitorConditionTypes.SyncRejectedRowsV2]: {
    EvaluationTrigger: EvaluationTrigger.Discrete,
    EvaluationType: EvaluationType.Point,
  },
  [MonitorConditionTypes.SyncSequentialFailures]: {
    EvaluationTrigger: EvaluationTrigger.Discrete,
    EvaluationType: EvaluationType.Point,
  },
  [MonitorConditionTypes.SyncDuration]: {
    EvaluationTrigger: EvaluationTrigger.Continuous,
    EvaluationType: EvaluationType.Batch,
  },
  [MonitorConditionTypes.SyncMissedSchedules]: {
    EvaluationTrigger: EvaluationTrigger.Continuous,
    EvaluationType: EvaluationType.Batch,
  },
  [MonitorConditionTypes.SyncThroughput]: {
    EvaluationTrigger: EvaluationTrigger.Continuous,
    EvaluationType: EvaluationType.Batch,
  },
  [MonitorConditionTypes.EventSourceVolume]: {
    EvaluationTrigger: EvaluationTrigger.Continuous,
    EvaluationType: EvaluationType.Batch,
  },
  [MonitorConditionTypes.ModelSize]: {
    EvaluationTrigger: EvaluationTrigger.Discrete,
    EvaluationType: EvaluationType.Point,
  },
  [MonitorConditionTypes.JourneySequentialFailures]: {
    EvaluationTrigger: EvaluationTrigger.Discrete,
    EvaluationType: EvaluationType.Point,
  },
  [MonitorConditionTypes.IDRRunFailure]: {
    EvaluationTrigger: EvaluationTrigger.Discrete,
    EvaluationType: EvaluationType.Point,
  },
  [MonitorConditionTypes.EventStreamingSyncLatency]: {
    EvaluationTrigger: EvaluationTrigger.Continuous,
    EvaluationType: EvaluationType.Batch,
  },
  [MonitorConditionTypes.EventStreamingSyncFailures]: {
    EvaluationTrigger: EvaluationTrigger.Continuous,
    EvaluationType: EvaluationType.Batch,
  },
  [MonitorConditionTypes.AIDRunError]: {
    EvaluationTrigger: EvaluationTrigger.Discrete,
    EvaluationType: EvaluationType.Point,
  },
  [MonitorConditionTypes.AIDRunDuration]: {
    EvaluationTrigger: EvaluationTrigger.Continuous,
    EvaluationType: EvaluationType.Batch,
  },
  [MonitorConditionTypes.AIDIdleTime]: {
    EvaluationTrigger: EvaluationTrigger.Continuous,
    EvaluationType: EvaluationType.Batch,
  },
  [MonitorConditionTypes.AIDLowInteractionVolume]: {
    EvaluationTrigger: EvaluationTrigger.Discrete,
    EvaluationType: EvaluationType.Point,
  },
  [MonitorConditionTypes.EventSourceLag]: {
    EvaluationTrigger: EvaluationTrigger.Continuous,
    EvaluationType: EvaluationType.Batch,
  },
} as const;

// All point monitors can have an eventTime which is the time the actual
// monitored event occurred. This is optional for testing.
type PointMetricValue<T> = T & { eventTime?: Date };

/**
 * This is where we define the property types for each monitor condition. Each
 * new condition *must* have properties defined.
 */
export type MonitorConditionProperties = {
  [MonitorConditionTypes.SyncRejectedRows]: {
    Threshold: Threshold<{
      rejectedRowCount: number | null;
      pctRejectedRows: number | null;
    }>;
    Value: PointMetricValue<{
      acceptedRows: number;
      rejectedRows: number;
      sampledError?: string;
    }>;
    Metadata: {
      error: string;
    };
  };
  [MonitorConditionTypes.SyncRejectedRowsV2]: {
    Threshold: Threshold<{
      method: "percentage" | "count";
      value: number;
    }>;
    Value: PointMetricValue<{
      acceptedRows: number;
      rejectedRows: number;
      resourceError?: string;
      sampledError?: string;
    }>;
    Metadata: {
      error: string;
    };
  };
  [MonitorConditionTypes.SyncSequentialFailures]: {
    Threshold: Threshold<number>;
    Value: PointMetricValue<{ count: number; error?: string }>;
    Metadata: {
      error: string;
    };
  };
  [MonitorConditionTypes.SyncDuration]: {
    Threshold: Threshold<number>;
    Value: {
      durationMinutes: number;
      previousDurationMinutes?: number;
      inProgress: boolean;
    };
    Metadata: never;
  };
  [MonitorConditionTypes.SyncMissedSchedules]: {
    Threshold: Threshold<number>;
    Value: number;
    Metadata: never;
  };
  [MonitorConditionTypes.SyncThroughput]: {
    Threshold: Threshold<
      { interval: ElementOf<typeof SyncThroughputInterval> } & (
        | {
            sign: "gt" | "lt";
            count: number;
          }
        | { sign: "gtlt"; min: number; max: number }
      )
    >;
    Value: {
      warnChanges: bigint;
      unhealthyChanges: bigint;
      /** Sync requests inside the warning and error windows. */
      warnRequests?: number;
      unhealthyRequests?: number;
      /**
       * Why the sync is no longer meant to run, if it isn't: its end date has
       * passed, or it is disabled. Absent when the sync is still expected to run,
       * in which case zero throughput is a real drop.
       */
      notRunningReason?: "expired" | "disabled";
    };
    Metadata: never;
  };
  [MonitorConditionTypes.EventSourceVolume]: {
    Threshold: Threshold<
      {
        interval: ElementOf<typeof EventSourceVolumeInterval>;
        sign: "gt" | "lt";
        count: number;
      }[]
    >;
    Value: {
      // ordered list of volume counts
      // order should match the order of Threshold configs
      warnChanges: number[];
      unhealthyChanges: number[];
    };
    Metadata: never;
  };
  [MonitorConditionTypes.ModelSize]: {
    Threshold: Threshold<
      {
        sign: "gt" | "lt";
        count: number;
      }[]
    >;
    Value: {
      size: number;
    };
    Metadata: never;
  };
  [MonitorConditionTypes.JourneySequentialFailures]: {
    Threshold: Threshold<number>;
    Value: PointMetricValue<{ count: number; error?: string }>;
    Metadata: {
      error: string;
    };
  };
  [MonitorConditionTypes.IDRRunFailure]: {
    Threshold: Threshold<boolean>;
    Value: PointMetricValue<{ isFailure: boolean; error?: string }>;
    Metadata: {
      error: string;
    };
  };
  [MonitorConditionTypes.EventStreamingSyncLatency]: {
    Threshold: Threshold<{
      interval: ElementOf<typeof EventStreamingSyncInterval>;
      value: number;
    }>;
    Value: { latencySeconds: number };
    Metadata: never;
  };
  [MonitorConditionTypes.EventStreamingSyncFailures]: {
    Threshold: Threshold<{
      interval: ElementOf<typeof EventStreamingSyncInterval>;
      method: "percentage" | "count";
      value: number;
    }>;
    Value: {
      totalEvents: number;
      failedEvents: number;
    };
    Metadata: never;
  };
  [MonitorConditionTypes.AIDRunError]: {
    Threshold: Threshold<number>;
    Value: PointMetricValue<{ count: number; error?: string }>;
    Metadata: { error: string };
  };
  [MonitorConditionTypes.AIDRunDuration]: {
    Threshold: Threshold<number>;
    Value: number;
    Metadata: never;
  };
  [MonitorConditionTypes.AIDIdleTime]: {
    Threshold: Threshold<number>;
    Value: number;
    Metadata: never;
  };
  [MonitorConditionTypes.AIDLowInteractionVolume]: {
    Threshold: Threshold<number>;
    Value: PointMetricValue<{ interactionCount: number }> | null;
    Metadata: never;
  };
  [MonitorConditionTypes.EventSourceLag]: {
    Threshold: Threshold<{
      value: number;
      interval: ElementOf<typeof EventSourceLagInterval>;
    }>;
    Value: {
      averageLag: number;
    };
    Metadata: never;
  };
};

export const EventSourceVolumeInterval = [
  "7 days",
  "2 days",
  "1 day",
  "4 hours",
  "1 hour",
  "15 minutes",
] as const;

export const SyncThroughputInterval = [
  "7 days",
  "2 days",
  "1 day",
  "1 hour",
  "4 hours",
] as const;

export const EventStreamingSyncInterval = [
  "7 days",
  "2 days",
  "1 day",
  "4 hours",
  "1 hour",
  "15 minutes",
] as const;

export const EventSourceLagInterval = [
  "7 days",
  "2 days",
  "1 day",
  "4 hours",
  "1 hour",
  "15 minutes",
] as const;

/**
 * If you are getting an error here, it's likely because you haven't implemented
 * properties properly for a new monitor condition type.
 */
export type MonitorConditionConfig = {
  [T in MonitorConditionTypes]: MonitorConditionProperties[T] &
    (typeof MonitorConditionEvaluationProperties)[T];
};

// Thresholds types from the condition jsonb values
export interface Threshold<T> {
  [MonitorStatus.Warning]?: T;
  [MonitorStatus.Unhealthy]?: T;
}

export type TypeFromThreshold<T extends Threshold<any>> = T[keyof T];

export type PointEvaluationContext = {
  resourceId: string;
  resourceType: MonitoredResourceType;
  workspaceId: string;
};

export type BatchEvaluationContext = {
  resourceType: MonitoredResourceType;
  monitorConditionType: MonitorConditionTypes;
};

export class AbortedEvaluation extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AbortedEvaluation";
  }
}

export type EvaluatedPointMonitorCondition<
  T extends MonitorConditionTypes,
  R extends MonitoredResourceType,
> = {
  evaluationType: EvaluationType.Point;
  getMetricValue(
    input: MonitoredResourceInputs[R],
    context: PointEvaluationContext,
    pgClient: Queryable,
    signal?: AbortSignal,
  ): Promise<MonitorConditionConfig[T]["Value"]>;
  evaluate(
    thresholds: MonitorConditionConfig[T]["Threshold"],
    value: MonitorConditionConfig[T]["Value"],
    context: PointEvaluationContext,
    signal?: AbortSignal,
  ): Promise<{
    status: MonitorStatus;
    resourceError?: string; // This field is for errors associated with the health of the resource e.g. the fatal sync error
  }>;
  // We would have better typing here but it borks the frontend if we import from /channels/types
  // so this will do for now.
  render: (
    opts: any,
  ) => Promise<{ text: string; metadata?: { error?: string } }>;
};

export type EvaluatedBatchMonitorCondition<T extends MonitorConditionTypes> = {
  batchSize?: number;
  evaluationType: EvaluationType.Batch;
  getMetricValues(
    context: BatchEvaluationContext,
    conditionConfigs: StoredMonitorCondition[],
    pgClient: ClientBase,
    signal?: AbortSignal,
  ): Promise<
    {
      value: MonitorConditionConfig[T]["Value"];
      conditionConfig: StoredMonitorCondition;
    }[]
  >;
  evaluate(
    thresholds: MonitorConditionConfig[T]["Threshold"],
    value: MonitorConditionConfig[T]["Value"],
    context: PointEvaluationContext,
    signal?: AbortSignal,
  ): Promise<{
    status: MonitorStatus;
    resourceError?: string; // This field is for errors associated with the health of the resource e.g. the fatal sync error
  }>;
  // We would have better typing here but it borks the frontend if we import from /channels/types
  // so this will do for now.
  render: (
    opts: any,
  ) => Promise<{ text: string; metadata?: { error?: string } }>;
};

export type TemplateOrManualConditionId = {
  conditionId?: string;
  conditionTemplateId?: string;
};

export interface MonitorConditionEvaluateAll {
  type: typeof MonitorOperationType.EvaluateAll;
  ids?: TemplateOrManualConditionId[];
  ignoreAlert?: boolean;
}

export type MonitorConditionEvaluate = {
  type: typeof MonitorOperationType.Evaluate;
  ids: TemplateOrManualConditionId[];
  ignoreAlert?: boolean;
};

export type MonitorConditionDelete = {
  type: typeof MonitorOperationType.Delete;
  ids: TemplateOrManualConditionId[];
  ignoreAlert?: boolean;
};

export type MonitorConditionDisable = {
  type: typeof MonitorOperationType.Disable;
  ids: TemplateOrManualConditionId[];
  ignoreAlert?: boolean;
};

export type MonitorConditionEnable = {
  type: typeof MonitorOperationType.Enable;
  ids: TemplateOrManualConditionId[];
  ignoreAlert?: boolean;
};

// Operations for conditions on a resource's monitor
export type MonitorConditionOperations =
  | MonitorConditionEvaluateAll
  | MonitorConditionEvaluate
  | MonitorConditionDelete
  | MonitorConditionDisable
  | MonitorConditionEnable;

export type MonitorEvaluatorMapping = {
  [R in MonitoredResourceType]: Partial<{
    [C in MonitorConditionTypes]:
      | EvaluatedPointMonitorCondition<C, R>
      | EvaluatedBatchMonitorCondition<C>;
  }>;
};

/**
 * To support a new monitor condition for a resource type, first add it here.
 *
 * Note: It's technically possible to generate this object from the
 * MonitorConditionEvaluators object; but the types that we get
 * out of it, while correct, are wildly unreadable in the editor.
 * They also can't be imported on the frontend.
 * This manual declaration is buying readability with a
 * little bit of boilerplate.
 */
export const SupportedConditionsForResourceTypes = {
  [MonitoredResourceType.Sync]: [
    MonitorConditionTypes.SyncSequentialFailures,
    MonitorConditionTypes.SyncRejectedRows,
    MonitorConditionTypes.SyncThroughput,
    MonitorConditionTypes.ModelSize,
    MonitorConditionTypes.SyncDuration,
    MonitorConditionTypes.SyncMissedSchedules,
  ],
  [MonitoredResourceType.EventSync]: [
    MonitorConditionTypes.SyncRejectedRowsV2,
    MonitorConditionTypes.SyncSequentialFailures,
  ],
  [MonitoredResourceType.EventSource]: [
    MonitorConditionTypes.EventSourceVolume,
    MonitorConditionTypes.EventSourceLag,
  ],
  [MonitoredResourceType.Journey]: [
    MonitorConditionTypes.JourneySequentialFailures,
  ],
  [MonitoredResourceType.IDR]: [MonitorConditionTypes.IDRRunFailure],
  [MonitoredResourceType.EventStreamingSync]: [
    MonitorConditionTypes.EventStreamingSyncLatency,
    MonitorConditionTypes.EventStreamingSyncFailures,
  ],
  [MonitoredResourceType.DecisionEngineFlow]: [
    MonitorConditionTypes.AIDRunError,
    MonitorConditionTypes.AIDRunDuration,
    MonitorConditionTypes.AIDIdleTime,
    MonitorConditionTypes.AIDLowInteractionVolume,
  ],
} as const;

export type SupportedConditionsForResourceTypes =
  typeof SupportedConditionsForResourceTypes;

export type SupportedConditionsForResourceType<
  T extends MonitoredResourceType,
> = SupportedConditionsForResourceTypes[T][number];

/**
 * The operation that occured to the table, needed to evaluate a v1 Alert
 *
 * Originally this data came straight out of the Hasura event that
 * was raised on changes to the sync_attempts table, basically a Postgres trigger.
 * When we removed the dependency on Hasura events, we needed to recreate
 * this information in the worker and then serialiaze it in the SQS message sent
 * to the resource-monitor service.
 */
export enum v1MonitorOperation {
  Update = "UPDATE",
  Insert = "INSERT",
}
/**
 * The details needed to evaluate a v1 Alert
 *
 * Originally this data came straight out of the Hasura event that
 * was raised on changes to the sync_attempts table, basically a Postgres trigger.
 * When we removed the dependency on Hasura events, we needed to recreate
 * this information in the worker and then serialiaze it in the SQS message sent
 * to the resource-monitor service.
 */
export type v1MonitorDetails = {
  operation: v1MonitorOperation;
  oldSyncAttemptStatus?: string;
  newSyncAttemptStatus: string;
};
